<template>
  <div class="walletStack" :style="{ minHeight: stackHeight + 'px' }">
    <div
      v-for="(v, i) in computedDisplayStack"
      :key="v.id"
      class="walletCard"
      :class="{ 
        expanded: expandedCardId === v.id,
        'initial-animation': showInitialAnimation && expandedCardId === v.id
      }"
      :style="getCardStyle(i, v.id, v.discount.cardColour)"
      @click="handleCardClick(v)"
    >
      <VoucherCard
        :voucher="v"
        :formatter="formatter"
        :wallet="true"
        :obscured="!(expandedCardId === v.id || i === computedDisplayStack.length - 1)"
        :cardCount="getCardCount(v) && expandedMerchantId !== v.discount.merchant.id ? getCardCount(v) : null"
        :totalGroupAmount="getGroupAmount(v) && expandedMerchantId !== v.discount.merchant.id ? getGroupAmount(v) : null"
      />
    </div>
  </div>
</template>

<script>
import VoucherCard from './VoucherCard.vue';
import Helpers from '../../mixins/Helpers';

export default {
  name: 'MGWalletStack',
  components: { VoucherCard },
  mixins: [Helpers],
  props: {
    allActiveVouchers: { type: Array, required: true },
    formatter: { type: Object, required: true },
    showInitialReveal: { type: Boolean, default: true },
  },
  data() {
    return {
      ungroupedMerchantId: null,
      expandedCardId: null,
      showInitialAnimation: false,
      isRegrouping: false,
      expandedMerchantId: null, // Track which merchant group is expanded
      normalOverlap: 70, // px - normal spacing between different merchants
      tightOverlap: 10, // px - much tighter spacing for same merchant cards
    };
  },
  computed: {
    groupedVouchers() {
      // Group activeVouchers by merchant.id and return an object for each group
      const groups = {};
      this.allActiveVouchers.forEach(v => {
        const id = v.discount.merchant.id;
        if (!groups[id]) {
          groups[id] = {
            merchant: v.discount.merchant,
            vouchers: [],
            total: 0,
            count: 0,
            firstVoucher: v
          };
        }
        groups[id].vouchers.push(v);
        groups[id].total += v.currentBalance;
        groups[id].count++;
      });
      // Return an array of group objects, using the first voucher as the display card
      return Object.values(groups).map(g => {
        return {
          ...g.firstVoucher,
          groupCount: g.count > 1 ? g.count : null,
          groupTotal: g.count > 1 ? g.total : null
        };
      });
    },
    computedDisplayStack() {
      // Default to showing all vouchers ungrouped but grouped by merchant
      if (!this.ungroupedMerchantId) {
        // Sort vouchers by merchant ID so same merchants appear together
        const sortedVouchers = [...this.allActiveVouchers].sort((a, b) => {
          return a.discount.merchant.id.localeCompare(b.discount.merchant.id);
        });
        
        // Group by merchant and limit to 3 cards per merchant when not expanded
        const result = [];
        let currentMerchant = null;
        let currentMerchantCards = [];
        
        for (const voucher of sortedVouchers) {
          if (currentMerchant !== voucher.discount.merchant.id) {
            // Add previous merchant's cards (limited to 3 if not expanded)
            if (currentMerchantCards.length > 0) {
              const isExpanded = this.expandedMerchantId === currentMerchant;
              const cardsToShow = isExpanded ? currentMerchantCards : currentMerchantCards.slice(0, 3);
              result.push(...cardsToShow);
            }
            
            // Start new merchant group
            currentMerchant = voucher.discount.merchant.id;
            currentMerchantCards = [voucher];
          } else {
            // Add to current merchant group
            currentMerchantCards.push(voucher);
          }
        }
        
        // Add the last merchant's cards
        if (currentMerchantCards.length > 0) {
          const isExpanded = this.expandedMerchantId === currentMerchant;
          const cardsToShow = isExpanded ? currentMerchantCards : currentMerchantCards.slice(0, 3);
          result.push(...cardsToShow);
        }
        
        return result;
      }
      
      const stack = [];
      this.groupedVouchers.forEach(groupedVoucher => {
        if (groupedVoucher.discount.merchant.id === this.ungroupedMerchantId) {
          stack.push(
            ...this.allActiveVouchers.filter(
              v => v.discount.merchant.id === this.ungroupedMerchantId
            )
          );
        } else {
          stack.push(groupedVoucher);
        }
      });
      return stack;
    },
    stackHeight() {
      const cardHeight = 200;
      const extraPadding = 0; 
      
      // Calculate cumulative position for the last card
      let cumulativePosition = 0;
      for (let i = 0; i < this.computedDisplayStack.length - 1; i++) {
        const currentCard = this.computedDisplayStack[i];
        const nextCard = this.computedDisplayStack[i + 1];
        if (nextCard && currentCard.discount.merchant.id === nextCard.discount.merchant.id) {
          cumulativePosition += this.tightOverlap; // Use tighter spacing for same merchant
        } else {
          cumulativePosition += this.normalOverlap; // Use normal spacing for different merchants
        }
      }
      
      return cumulativePosition + cardHeight + extraPadding;
    },
  },
  mounted() {
    this.triggerInitialAnimation();
  },
  methods: {
    triggerInitialAnimation() {
      // Don't animate if the prop is set to false
      if (!this.showInitialReveal) {
        return;
      }
      
      // Only animate if there are grouped vouchers
      if (this.groupedVouchers.length > 0) {
        const firstGroup = this.groupedVouchers[0];
        
        // Check if this is actually a group (has groupCount > 1)
        if (firstGroup.groupCount && firstGroup.groupCount > 1) {
          // Start animation after a short delay
          setTimeout(() => {
            this.showInitialAnimation = true;
            this.ungroupedMerchantId = firstGroup.discount.merchant.id;
            
            // After 1.5 seconds, start re-grouping with smooth transition
            setTimeout(() => {
              this.isRegrouping = true;
              // Small delay to ensure the regrouping state is applied
              setTimeout(() => {
                this.ungroupedMerchantId = null;
                // Wait for the transition to complete before cleaning up
                setTimeout(() => {
                  this.showInitialAnimation = false;
                  this.isRegrouping = false;
                }, 400); // Match the transition duration
              }, 50);
            }, 1500);
          }, 1000);
        } else {
          // Let's find any group that has multiple vouchers
          const groupedVouchers = this.groupedVouchers.filter(v => v.groupCount && v.groupCount > 1);
          
          if (groupedVouchers.length > 0) {
            const firstGroupedVoucher = groupedVouchers[0];
            
            // Start animation after a short delay
            setTimeout(() => {
              this.showInitialAnimation = true;
              this.ungroupedMerchantId = firstGroupedVoucher.discount.merchant.id;
              
              // After 1.5 seconds, start re-grouping with smooth transition
              setTimeout(() => {
                this.isRegrouping = true;
                // Small delay to ensure the regrouping state is applied
                setTimeout(() => {
                  this.ungroupedMerchantId = null;
                  // Wait for the transition to complete before cleaning up
                  setTimeout(() => {
                    this.showInitialAnimation = false;
                    this.isRegrouping = false;
                  }, 400); // Match the transition duration
                }, 50);
              }, 1000);
            }, 500);
          }
        }
      }
    },
    getCardStyle(index, cardId, cardColour) {
      const isExpanded = this.expandedCardId === cardId;
      const expandedCardIndex = this.computedDisplayStack.findIndex(card => card.id === this.expandedCardId);
      
      let backgroundColor = cardColour || 'var(--background-color-vourcher-default)'; 
      let color = this._getTextColorForBackground(backgroundColor);
      
      // Calculate cumulative position based on actual spacing used
      let cumulativePosition = 0;
      for (let i = 0; i < index; i++) {
        const currentCard = this.computedDisplayStack[i];
        const nextCard = this.computedDisplayStack[i + 1];
        if (nextCard && currentCard.discount.merchant.id === nextCard.discount.merchant.id && currentCard.discount.merchant.id !== this.expandedMerchantId) {
          cumulativePosition += this.tightOverlap; // Use tighter spacing for same merchant (unless expanded)
        } else {
          cumulativePosition += this.normalOverlap; // Use normal spacing for different merchants or expanded merchant
        }
      }
      
      // Calculate top position with improved animation logic
      let topPosition;
      if (isExpanded) {
        // Selected card moves up with a slight offset for visual appeal, but ensure it doesn't go under header
        const expandedPosition = Math.max(cumulativePosition - 30, 20); // Minimum 20px from top
        topPosition = `${expandedPosition}px`;
      } else if (this.expandedCardId && index > expandedCardIndex) {
        // Cards below the selected card slide down much more to reveal the selected card
        const extraOffset = 110; // more space to reveal the selected card
        topPosition = `${cumulativePosition + extraOffset}px`;
      } else {
        // Normal positioning for unselected cards
        topPosition = `${cumulativePosition}px`;
      }
      
      let style = {
        position: 'absolute',
        top: topPosition,
        width: isExpanded ? '104%' : '100%',
        height: '200px',
        zIndex: isExpanded ? 999 : index,
        background: backgroundColor,
        color: color,
        transition: this.getTransitionStyle(isExpanded, index, expandedCardIndex)
      };
      
      if (isExpanded) {
        // For expanded card, use left positioning with negative margin to center the 104% width
        style.left = '-2%';
        style.right = 'auto';
        style.margin = '0';
      } else {
        // For normal cards, use the standard centering
        style.left = 0;
        style.right = 0;
        style.margin = '0 auto';
      }
      
      return style;
    },
    getTransitionStyle(isExpanded, currentIndex, expandedCardIndex) {
      // During initial animation regrouping, use smooth transitions
      if (this.isRegrouping) {
        return 'top 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
               'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      }
      
      if (isExpanded) {
        // Selected card: smooth expansion with delayed z-index to be revealed by dropping cards
        return 'top 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
               'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
               'z-index 0.1s ease-out 0.2s'; // Delay z-index change so card is revealed naturally
      } else if (this.expandedCardId && currentIndex > expandedCardIndex) {
        // Cards below selected: drop much faster to reveal the selected card
        const delay = (currentIndex - expandedCardIndex) * 0.02; // Faster staggered delay
        return `top 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}s`; // Faster drop
      } else {
        // Other cards: standard transition
        return 'top 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
               'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      }
    },
    goToVoucher(voucher) {
			this.$router.push('/MyVouchers/Merchants/' + voucher.id + '?fromWallet=true');
    },
    handleCardClick(v) {
      // Don't allow interaction during initial animation
      if (this.showInitialAnimation) {
        return;
      }
      
      // Don't allow interaction with skeleton vouchers
      if (v.isSkeleton) {
        return;
      }
      
      // Check if this card is part of a group
      const merchantId = v.discount.merchant.id;
      const merchantVouchers = this.allActiveVouchers.filter(v => v.discount.merchant.id === merchantId);
      const isGrouped = merchantVouchers.length > 1;
      
      // Check if this is the top (non-obscured) card
      const isTopCard = this.computedDisplayStack[this.computedDisplayStack.length - 1].id === v.id;
      
      // If it's the top card
      if (isTopCard) {
        // If it's grouped and the group hasn't been expanded yet, expand the group first
        if (isGrouped && this.expandedMerchantId !== merchantId) {
          this.expandedMerchantId = merchantId;
          this.expandedCardId = null;
          return;
        }
        
        // If another card is currently selected (but not this merchant group), deselect it first
        if (this.expandedCardId !== null || (this.expandedMerchantId !== null && this.expandedMerchantId !== merchantId)) {
          this.expandedCardId = null;
          this.expandedMerchantId = null;
          return; // Don't navigate yet, just deselect
        }
        
        // If no other card is selected, or if this group is already expanded, navigate to the voucher
        this.goToVoucher(v);
        return;
      }
      
      if (isGrouped) {
        // For grouped cards, toggle the expanded merchant ID instead of expanding the card
        if (this.expandedMerchantId === merchantId) {
          // If the group is already expanded, check if this card is in the foreground
          if (this.expandedCardId === v.id) {
            this.goToVoucher(v); // Navigate to voucher
          } else {
            this.expandedCardId = v.id; // Bring card to the foreground
          }
        } else {
          // If clicking on a different group, collapse the previous group and expand the new one
          this.expandedMerchantId = merchantId; // Expand the new group
          this.expandedCardId = null; // Clear any foreground card
        }
      } else {
        // For single cards, collapse any expanded group first
        if (this.expandedMerchantId) {
          this.expandedMerchantId = null;
          this.expandedCardId = null;
        }
        
        // Then handle single card expansion
        if (this.expandedCardId === v.id) {
          this.goToVoucher(v);
        } else {
          this.expandedCardId = v.id;
        }
      }
    },
    getCardCount(v) {
      // Check if this voucher is part of a group
      const merchantId = v.discount.merchant.id;
      const merchantVouchers = this.allActiveVouchers.filter(v => v.discount.merchant.id === merchantId);
      return merchantVouchers.length > 1 ? merchantVouchers.length : null;
    },
    getGroupAmount(v) {
      // Calculate total amount for grouped vouchers
      const merchantId = v.discount.merchant.id;
      const merchantVouchers = this.allActiveVouchers.filter(v => v.discount.merchant.id === merchantId);
      if (merchantVouchers.length > 1) {
        return merchantVouchers.reduce((total, v) => total + v.currentBalance, 0);
      }
      return null;
    },
  }
};
</script>

<style scoped>
.walletStack {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100px; /* Ensure minimum height for the stack */
  padding-top: 60px; /* Add more top padding to prevent expanded cards from going under header */
}

.walletCard {
  position: absolute;
  width: 98%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 24px 0 rgba(0,0,0,0.18), 0 1.5px 4px 0 rgba(0,0,0,0.10);
  will-change: top, transform, width, height, box-shadow, opacity;
  overflow: hidden;
  display: flex;
  align-items: stretch;
}

.walletCard.expanded {
  z-index: 99 !important;
  cursor: default;
}

.walletCard.initial-animation {
  box-shadow: 0 8px 24px 0 rgba(0,0,0,0.25), 0 1.5px 4px 0 rgba(0,0,0,0.15), 0 0 20px rgba(0,123,255,0.3);
  animation: initialGlow 3s ease-in-out;
}

@keyframes initialGlow {
  0%, 100% { 
    box-shadow: 0 8px 24px 0 rgba(0,0,0,0.18), 0 1.5px 4px 0 rgba(0,0,0,0.10);
  }
  50% { 
    box-shadow: 0 8px 24px 0 rgba(0,0,0,0.25), 0 1.5px 4px 0 rgba(0,0,0,0.15), 0 0 20px rgba(0,123,255,0.3);
  }
}

.card {
  color: var(--light-text-color); 
  padding: 10px 16px 10px 16px; /* less padding for compact hidden view */
  border-radius: 10px;
  min-height: 210px;
}

.walletCard.expanded .card {
  padding-bottom: 48px !important;
  margin-top: 10px;
}
</style>